/**
 * AST 기반 자동 생성된 검증 함수
 * 이 파일은 scripts/generate-validators.ts에 의해 자동 생성됩니다.
 * 수동으로 수정하지 마세요.
 * 
 * 생성 시간: 2026-10-07T08:44:05.530Z
 * 소스 파일: src/data/dto/admin/doctor-treatment-summary-dto
 */

import type { DoctorTreatmentSummaryDto } from "@/data/dto/admin/doctor-treatment-summary-dto";

/**
 * DoctorTreatmentSummaryDto 타입 가드 함수
 * AST 분석을 통해 자동 생성된 최적화된 검증 함수
 */
export function isDoctorTreatmentSummaryDto(input: unknown): input is DoctorTreatmentSummaryDto {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    return false;
  }
  
  const obj = input as Record<string, unknown>;
  
  return (
    typeof obj.createdAt === "string" &&
    typeof obj.patientId === "string" &&
    typeof obj.treatmentUuid === "string" &&
    typeof obj.patientUuid === "string" &&
    (obj.step == null || (typeof obj.step === "number" && !isNaN(obj.step))) &&
    typeof obj.type === "number" && !isNaN(obj.type)
  );
}

/**
 * DoctorTreatmentSummaryDto 상세 검증 결과 반환 함수
 * 검증 실패 시 상세한 에러 정보를 반환합니다.
 */
export interface IValidation<T> {
  success: boolean;
  data?: T;
  errors?: Array<{ path: string; expected: string; value: unknown }>;
}

export function validateDoctorTreatmentSummaryDto(input: unknown): IValidation<DoctorTreatmentSummaryDto> {
  const errors: Array<{ path: string; expected: string; value: unknown }> = [];
  
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    errors.push({
      path: "",
      expected: "object",
      value: input,
    });
    return {
      success: false,
      errors,
    };
  }
  
  const obj = input as Record<string, unknown>;
  
    if (typeof obj !== "object" || obj === null || Array.isArray(obj)) {
      errors.push({ path: "", expected: "object", value: obj });
    } else {
      const objObj = obj as Record<string, unknown>;
      // createdAt (required)
      if (objObj.createdAt === undefined) {
        errors.push({ path: "createdAt", expected: "string", value: undefined });
      } else {
        if (typeof objObj.createdAt !== "string") {
          errors.push({ path: "createdAt", expected: "string", value: objObj.createdAt });
        }
      }
      // patientId (required)
      if (objObj.patientId === undefined) {
        errors.push({ path: "patientId", expected: "string", value: undefined });
      } else {
        if (typeof objObj.patientId !== "string") {
          errors.push({ path: "patientId", expected: "string", value: objObj.patientId });
        }
      }
      // treatmentUuid (required)
      if (objObj.treatmentUuid === undefined) {
        errors.push({ path: "treatmentUuid", expected: "string", value: undefined });
      } else {
        if (typeof objObj.treatmentUuid !== "string") {
          errors.push({ path: "treatmentUuid", expected: "string", value: objObj.treatmentUuid });
        }
      }
      // patientUuid (required)
      if (objObj.patientUuid === undefined) {
        errors.push({ path: "patientUuid", expected: "string", value: undefined });
      } else {
        if (typeof objObj.patientUuid !== "string") {
          errors.push({ path: "patientUuid", expected: "string", value: objObj.patientUuid });
        }
      }
      // step (required)
      if (objObj.step === undefined) {
        errors.push({ path: "step", expected: "number", value: undefined });
      } else {
        if (!(objObj.step == null || (typeof objObj.step === "number" && !isNaN(objObj.step)))) {
          errors.push({ path: "step", expected: "number | null", value: objObj.step });
        }
      }
      // type (required)
      if (objObj.type === undefined) {
        errors.push({ path: "type", expected: "number", value: undefined });
      } else {
        if (!(typeof objObj.type === "number" && !isNaN(objObj.type))) {
          errors.push({ path: "type", expected: "number", value: objObj.type });
        }
      }
    }
  
  if (errors.length === 0 && isDoctorTreatmentSummaryDto(input)) {
    return {
      success: true,
      data: input,
    };
  }
  
  return {
    success: false,
    errors: errors.length > 0 ? errors : [
      {
        path: "",
        expected: "DoctorTreatmentSummaryDto",
        value: input,
      },
    ],
  };
}

/**
 * DoctorTreatmentSummaryDto 검증 후 에러 throw 함수
 * 검증 실패 시 상세한 에러 정보를 포함하여 에러를 throw합니다.
 */
export function assertDoctorTreatmentSummaryDto(input: unknown): DoctorTreatmentSummaryDto {
  const validation = validateDoctorTreatmentSummaryDto(input);
  if (!validation.success) {
    const errorDetails = validation.errors?.map(err => 
      `  - ${err.path}: 예상 타입 ${err.expected}, 실제 값: ${JSON.stringify(err.value)}`
    ).join("\n") || "알 수 없는 오류";
    throw new Error(`[assertDoctorTreatmentSummaryDto] :: 검증 실패: DoctorTreatmentSummaryDto 타입과 일치하지 않습니다\n\n오류 상세:\n${errorDetails}`);
  }
  return validation.data!;
}
